import React, { useMemo, useRef } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Slider } from '../ui/slider';
import { Input } from '../ui/input';

// Continuous, logarithmic RPM slider. Range is 0..300, positive only --
// direction is controlled separately via the "Reverse spin" toggle. The
// physical slider position is linear in "raw" units; RPM is derived from
// raw via a log curve so slow speeds (under ~50 RPM) aren't crammed into
// a few pixels.
const RAW_MAX = 1000;
const DEAD_ZONE = 30;
const RPM_MIN = 1;
const RPM_MAX = 300;
const SNAP_RPMS = [16 + 2 / 3, 33 + 1 / 3, 45, 78];
const SNAP_RAW_TOLERANCE = 14;
const HIGH_RPM_WARNING_THRESHOLD = 120;

function rpmAbsToRaw(rpmAbs) {
  const clamped = Math.max(RPM_MIN, Math.min(RPM_MAX, rpmAbs));
  const t = Math.log(clamped / RPM_MIN) / Math.log(RPM_MAX / RPM_MIN);
  return DEAD_ZONE + t * (RAW_MAX - DEAD_ZONE);
}

function rawAbsToRpm(rawAbs) {
  const clamped = Math.max(DEAD_ZONE, Math.min(RAW_MAX, rawAbs));
  const t = (clamped - DEAD_ZONE) / (RAW_MAX - DEAD_ZONE);
  return RPM_MIN * Math.pow(RPM_MAX / RPM_MIN, t);
}

export function rpmToSlider(rpm) {
  return rpmAbsToRaw(Math.max(0, rpm));
}

export function sliderToRpm(raw) {
  return rawAbsToRpm(Math.max(0, raw));
}

const SNAP_RAWS = SNAP_RPMS.map((r) => ({ rpm: r, raw: rpmAbsToRaw(r) }));

function applySnap(raw) {
  for (const s of SNAP_RAWS) {
    if (Math.abs(raw - s.raw) <= SNAP_RAW_TOLERANCE) {
      return s.raw;
    }
  }
  return raw;
}

export default function RpmSlider({ rpm, onChange }) {
  const draggingRef = useRef(false);
  const rawValue = useMemo(() => rpmToSlider(rpm), [rpm]);
  const isHighSpeed = rpm > HIGH_RPM_WARNING_THRESHOLD;

  const handleChange = (vals) => {
    const raw = applySnap(vals[0]);
    const nextRpm = sliderToRpm(raw);
    onChange(nextRpm);
  };

  const handleNumberInput = (e) => {
    const val = Number(e.target.value);
    if (!Number.isFinite(val)) return;
    const clamped = Math.max(RPM_MIN, Math.min(RPM_MAX, val));
    onChange(clamped);
  };

  const warningText = 'Above 120 RPM, rotation approaches the temporal sampling limit at 30fps and artwork can alias -- appearing to stutter or spin backward (the "wagon-wheel" effect). The app renders these speeds at 60fps to reduce it, but heavily textured or off-center artwork will still alias worse than a clean, centered label.';

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-xs font-medium text-neutral-600">Speed</label>
        <div className="flex items-center gap-1.5">
          <Input
            type="number"
            step="0.01"
            min={0}
            max={RPM_MAX}
            value={Number(rpm.toFixed(2))}
            onChange={handleNumberInput}
            className="h-7 w-20 text-xs font-mono text-right"
            data-testid="rpm-number-input"
          />
          <span className="text-xs text-neutral-400">RPM</span>
        </div>
      </div>

      <Slider
        min={0}
        max={RAW_MAX}
        step={1}
        value={[rawValue]}
        onValueChange={handleChange}
        onPointerDown={() => { draggingRef.current = true; }}
        onPointerUp={() => { draggingRef.current = false; }}
        data-testid="rpm-slider"
      />

      <div className="flex justify-between text-[10px] text-neutral-400 font-mono mt-1">
        <span>0</span>
        <span>300</span>
      </div>

      <div className="flex flex-wrap gap-1.5 mt-2">
        {SNAP_RPMS.map((r) => (
          <button
            key={r}
            type="button"
            onClick={() => onChange(r)}
            className={`text-[11px] px-2 py-1 rounded-md border transition-colors ${
              Math.abs(rpm - r) < 0.05 ? 'border-orange-300 bg-orange-50 text-orange-700' : 'border-neutral-200 text-neutral-500 hover:bg-neutral-50'
            }`}
            data-testid={`rpm-snap-${r === 16 + 2 / 3 ? '16-2-3' : r === 33 + 1 / 3 ? '33-1-3' : r}`}
          >
            {r === 16 + 2 / 3 ? '16 2/3' : r === 33 + 1 / 3 ? '33 1/3' : r}
          </button>
        ))}
      </div>

      {isHighSpeed && (
        <div className="mt-2.5 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2.5">
          <AlertTriangle className="w-3.5 h-3.5 text-amber-600 mt-0.5 flex-shrink-0" />
          <p className="text-[11px] text-amber-800 leading-relaxed">{warningText}</p>
        </div>
      )}
    </div>
  );
}
